import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField, 
  Grid,
  Box
} from '@mui/material';
import apiClient from "../../request/http-common";
import { useMutation } from "react-query";

const EditCustomPlan = ({ open, handleClose, customPlan, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    amount_invested: '',
    min_amount: '',
    bonus_per_downline: '',
    number_of_downlines: '',
    total_earning: '',
    initial_earning: '',
    duration: '',
    amount_per_hour: '',
    extra_note: ''
  });

  useEffect(() => {
    if (customPlan) {
      setFormData({
        name: customPlan.name || '',
        amount_invested: customPlan.amount_invested || '',
        min_amount: customPlan.min_amount || '',
        bonus_per_downline: customPlan.bonus_per_downline || '',
        number_of_downlines: customPlan.number_of_downlines || '',
        total_earning: customPlan.total_earning || '',
        initial_earning: customPlan.initial_earning || '',
        duration: customPlan.duration || '',
        amount_per_hour: customPlan.amount_per_hour || '',
        extra_note: customPlan.extra_note || ''
      });
    }
  }, [customPlan]);

  const { mutate: updateCustomPlan, isLoading } = useMutation(
    async () => {
      return await apiClient.post(`/api/custom-plans/${customPlan.id}`, formData);
    },
    {
      onSuccess: (res) => {
        if (res.data.status === 'success') {
          localStorage.setItem('notification', JSON.stringify({
            message: res.data.message,
            type: 'success'
          }));
          const event = new Event('newMessage');
          window.dispatchEvent(event);
          handleClose();
          if (onSuccess) onSuccess();
        }
      },
      onError: (err) => {
        const errorMessage = err.response?.data.message || 'An error occurred';
        localStorage.setItem('notification', JSON.stringify({
          message: errorMessage,
          type: 'error'
        }));
        const event = new Event('newMessage');
        window.dispatchEvent(event);
      }
    }
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Custom Investment Plan</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Plan Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Maximum Amount"
                name="amount_invested"
                type="number"
                value={formData.amount_invested}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Minimum Amount"
                name="min_amount"
                type="number"
                value={formData.min_amount}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Bonus Per Downline (%)"
                name="bonus_per_downline"
                type="number"
                value={formData.bonus_per_downline}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Number of Downlines"
                name="number_of_downlines"
                type="number"
                value={formData.number_of_downlines}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Total Earning (%)"
                name="total_earning"
                type="number"
                value={formData.total_earning}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Initial Earning (%)"
                name="initial_earning"
                type="number"
                value={formData.initial_earning}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Duration (comma separated)"
                name="duration"
                value={formData.duration}
                onChange={handleChange}
                required
                helperText="Enter durations separated by commas"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Amount Per Hour"
                name="amount_per_hour"
                type="number"
                value={formData.amount_per_hour}
                onChange={handleChange}
                required
                helperText="Enter the hourly earning rate"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Extra Note (Optional)"
                name="extra_note"
                value={formData.extra_note}
                onChange={handleChange}
                multiline
                rows={3}
                helperText="This note will appear below the Invest Now button"
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button 
          onClick={updateCustomPlan} 
          variant="contained" 
          disabled={isLoading}
        >
          {isLoading ? 'Updating...' : 'Update Custom Plan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditCustomPlan;

